import { useCallback, useRef } from 'react'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'
import {
  DashboardCell,
  DashboardConfig,
  DashboardLayout,
  MultiTable,
  MultiTableConfigTable,
} from '../types/gotypes'
import { activePageId, resolvePageLayout } from '../utils/dashboard'
import { extractErrorMessage } from '../utils/apitools'

/**
 * Which cell the edit modal is saving. Inner indices target a nested layout —
 * and, since #1080, a report pillar: a pillar projects entry k onto inner row
 * k, cell 0, so `innerRowIndex` addresses its entries with no new target shape.
 */
export interface CellSaveTarget {
  rowIndex: number
  cellIndex: number
  innerRowIndex?: number
  innerCellIndex?: number
}

/**
 * `saved` — committed. `failed` — the multi-table write failed, so nothing was
 * committed and the modal should stay open. `dropped` — the cell is no longer
 * there to save onto; the modal closes and the user is told. `saved-not-run`
 * — the card itself committed, but the first-ever analysis run it triggers
 * was refused or otherwise failed to start (#1219 gate 2); the caller shows
 * `onAnalysisStartFailed`'s message so the modal closing doesn't read as
 * "it worked."
 */
export type CellSaveResult = 'saved' | 'failed' | 'dropped' | 'saved-not-run'

export interface UseDashboardCellSaveProps {
  /** Reads the LIVE editor config — never a captured value. */
  getConfig: () => DashboardConfig
  /** Reads the LIVE active page index. */
  getActivePageIndex: () => number
  /** #1810: the filter map a Gen AI card with this job sends on the active
   *  page — the map the card compares its analysis's snapshot with. */
  getPageJobFilters: (widgetJob: string) => Record<string, string>
  /** Commits a layout onto the page `pageId` names. */
  commitLayout: (layout: DashboardLayout, pageId: string | undefined) => void
  customer: string
  /** Fallback job for widgets that don't carry their own. */
  job: string
  createMultiTable: (input: { tables: MultiTableConfigTable[]; name?: string }) => Promise<MultiTable>
  updateMultiTable: (input: { id: string; config: { tables: MultiTableConfigTable[]; name?: string } }) => Promise<MultiTable>
  /** Reports a save that could not land, for the user. */
  onDropped: (reason: string) => void
  /** #1219 gate 2: reports a refused (or otherwise failed) first-save
   *  analysis start — previously swallowed to a console.error only. The
   *  card itself is still saved; only the run didn't start. */
  onAnalysisStartFailed: (message: string) => void
}

const CELL_GONE = 'That widget was deleted while the save was in flight, so the change was not saved.'
const PAGE_GONE = 'That page was deleted while the save was in flight, so the change was not saved.'

/**
 * Saves one cell out of the edit modal.
 *
 * Saving a multi-table analysis card is four or more HTTP round-trips
 * (create/update the multi-table, list its analyses, delete each one, start a
 * fresh analysis), and the user can paste — itself a multi-second fork — during
 * them. So this follows exactly the rule `useDashboardDuplicate` follows: the page
 * is captured by id BEFORE the awaits, the layout is re-read from live state
 * AFTER them, and the destination row/cell is re-checked rather than assumed.
 * Committing a layout built before the awaits would replace the whole page and
 * wipe whatever else landed in the meantime.
 */
export function useDashboardCellSave(props: UseDashboardCellSaveProps) {
  const propsRef = useRef(props)
  propsRef.current = props

  const saveCell = useCallback(async (target: CellSaveTarget, cell: DashboardCell): Promise<CellSaveResult> => {
    const { getConfig, getActivePageIndex, commitLayout, customer, job, onDropped, onAnalysisStartFailed } = propsRef.current
    const { rowIndex, cellIndex } = target
    // Captured before the first await — an index would stop meaning this page.
    const pageId = activePageId(getConfig(), getActivePageIndex())

    // @ts-ignore - _pendingTables is a temporary property passed from CellEditModal
    const pendingTables: MultiTableConfigTable[] | undefined = cell._pendingTables
    let finalCell = { ...cell }
    // #1219 gate 2: the cell can still commit even when the first-ever
    // analysis run it triggers didn't start — 'saved-not-run' says so instead
    // of the swallowed console.error this used to be.
    let saveOutcome: 'saved' | 'saved-not-run' = 'saved'

    if (cell.widget_type === 'multi_table_analysis' && pendingTables && pendingTables.length > 0) {
      try {
        const existingMultiTableId = cell.multi_table_analysis_data?.multi_table_id
        let multiTableId: string

        if (existingMultiTableId) {
          try {
            await propsRef.current.updateMultiTable({
              id: existingMultiTableId,
              config: { tables: pendingTables, name: 'Dashboard Analysis' },
            })
            multiTableId = existingMultiTableId
          } catch (updateErr) {
            // #1282: the card names a multi-table this dashboard does not own —
            // a copy made before the server fork fix, whose id belongs to the
            // source. The update 404s. Repair in place: create a fresh
            // multi-table for this dashboard and repoint the card, so
            // re-picking tables and Save works instead of failing silently.
            if (axios.isAxiosError(updateErr) && updateErr.response?.status === 404) {
              const repaired = await propsRef.current.createMultiTable({
                tables: pendingTables,
                name: 'Dashboard Analysis',
              })
              multiTableId = repaired.id
            } else {
              throw updateErr
            }
          }
        } else {
          const createdMultiTable = await propsRef.current.createMultiTable({
            tables: pendingTables,
            name: 'Dashboard Analysis',
          })
          multiTableId = createdMultiTable.id
        }

        // Keep every other field the widget carries (prompt, title, output_length…).
        const analysisJob = pendingTables[0]?.job || job
        finalCell = {
          ...cell,
          multi_table_analysis_data: {
            ...cell.multi_table_analysis_data,
            multi_table_id: multiTableId,
            job: analysisJob,
            persona: cell.multi_table_analysis_data?.persona || 'Executive Summarizer',
          },
        }

        // Only the very first save auto-runs the analysis. Once a multi-table
        // exists, Save must never silently regenerate (#970 Part B) — the
        // user explicitly regenerates via the card's ↻ button, and the
        // previous output stays on screen until they do. The wizard (#970
        // Part A) already runs its own live preview against a fresh
        // multi-table before Accept + Save when a real dashboard id is
        // available, so this branch only fires for the "no live preview yet"
        // case — e.g. the first widget on a dashboard that hasn't been saved
        // yet — which is why it must carry the same instructions/context the
        // user actually configured, not just the persona.
        if (!existingMultiTableId) {
          try {
            await axios.post(
              `${API_BASE_URL}/multitables/${customer}/${analysisJob}/${multiTableId}/analysis/start`,
              {
                persona: cell.multi_table_analysis_data?.persona || 'Executive Summarizer',
                user_context: cell.multi_table_analysis_data?.user_context || '',
                synthesis_user_context: cell.multi_table_analysis_data?.synthesis_user_context || '',
                output_length: cell.multi_table_analysis_data?.output_length || 'standard',
                analysis_instructions: cell.multi_table_analysis_data?.analysis_instructions || '',
                synthesis_instructions: cell.multi_table_analysis_data?.synthesis_instructions || '',
                // #1810: the first run snapshots the page's filters.
                job_filters: propsRef.current.getPageJobFilters(analysisJob),
              }
            )
          } catch (analysisErr) {
            // #1219 gate 2: non-fatal to the SAVE — the card still commits and
            // the widget falls back to "No analysis yet" — but no longer
            // silent. A refusal (or any other failure) is reported through
            // `onAnalysisStartFailed` and the returned status, instead of
            // only a console.error nobody but a developer ever sees.
            console.error('Failed to start analysis:', analysisErr)
            saveOutcome = 'saved-not-run'
            onAnalysisStartFailed(extractErrorMessage(analysisErr))
          }
        }
      } catch (error) {
        console.error('Failed to create/update multi-table:', error)
        // Don't save the cell if multi-table creation failed
        return 'failed'
      }
    }

    // A pillar can carry cards added inside its own editor (#1080). Each one
    // needs its multi-table created before the cell is committed, exactly as a
    // top-level Gen AI card does — the failure rule is the same too: if the
    // write fails, nothing is committed and the modal stays open.
    // @ts-ignore - _pillarPendingTables is a temporary property from CellEditModal
    const pillarPending: Record<number, MultiTableConfigTable[]> | undefined = cell._pillarPendingTables
    if (cell.widget_type === 'report_pillar' && pillarPending && finalCell.report_pillar_data) {
      const entries = [...finalCell.report_pillar_data.entries]
      for (const [indexKey, tables] of Object.entries(pillarPending)) {
        const index = Number(indexKey)
        const entry = entries[index]
        if (!entry || !tables || tables.length === 0) continue
        if (entry.cell.multi_table_analysis_data?.multi_table_id) continue
        try {
          const created = await propsRef.current.createMultiTable({ tables, name: 'Dashboard Analysis' })
          entries[index] = {
            ...entry,
            cell: {
              ...entry.cell,
              multi_table_analysis_data: {
                ...entry.cell.multi_table_analysis_data,
                multi_table_id: created.id,
                job: tables[0]?.job || job,
                persona: entry.cell.multi_table_analysis_data?.persona || 'Executive Summarizer',
              },
            },
          }
        } catch (error) {
          console.error('Failed to create multi-table for a pillar card:', error)
          return 'failed'
        }
      }
      finalCell = {
        ...finalCell,
        report_pillar_data: { ...finalCell.report_pillar_data, entries },
      }
    }

    // Remove the temporary _pendingTables properties
    // @ts-ignore
    delete finalCell._pendingTables
    // @ts-ignore
    delete finalCell._pillarPendingTables

    // Everything from here is rebuilt on live state, read AFTER the awaits.
    const layout = resolvePageLayout(propsRef.current.getConfig(), pageId)
    if (!layout) {
      onDropped(PAGE_GONE)
      return 'dropped'
    }

    if (target.innerRowIndex !== undefined && target.innerCellIndex !== undefined) {
      const outerCell = layout.rows[rowIndex]?.cells[cellIndex]

      // Saving one entry of a report pillar (#1080).
      if (outerCell?.widget_type === 'report_pillar') {
        const entries = outerCell.report_pillar_data?.entries
        if (!entries?.[target.innerRowIndex] || target.innerCellIndex !== 0) {
          onDropped(CELL_GONE)
          return 'dropped'
        }
        const updatedOuterCell: DashboardCell = {
          ...outerCell,
          report_pillar_data: {
            ...outerCell.report_pillar_data!,
            entries: entries.map((entry, i) => (i === target.innerRowIndex ? { ...entry, cell: finalCell } : entry)),
          },
        }
        const rows = layout.rows.map((row, i) => {
          if (i !== rowIndex) return row
          return { ...row, cells: row.cells.map((c, j) => (j === cellIndex ? updatedOuterCell : c)) }
        })
        commitLayout({ ...layout, rows }, pageId)
        return saveOutcome
      }

      // Saving an inner cell within a nested layout
      if (!outerCell?.layout_data?.rows[target.innerRowIndex]?.cells[target.innerCellIndex]) {
        onDropped(CELL_GONE)
        return 'dropped'
      }
      const newInnerRows = outerCell.layout_data.rows.map((innerRow, iri) => {
        if (iri !== target.innerRowIndex) return innerRow
        return {
          ...innerRow,
          cells: innerRow.cells.map((ic, ici) => (ici === target.innerCellIndex ? finalCell : ic)),
        }
      })
      const updatedOuterCell = { ...outerCell, layout_data: { ...outerCell.layout_data, rows: newInnerRows } }
      const newRows = layout.rows.map((row, i) => {
        if (i !== rowIndex) return row
        return { ...row, cells: row.cells.map((c, j) => (j === cellIndex ? updatedOuterCell : c)) }
      })
      commitLayout({ ...layout, rows: newRows }, pageId)
      return saveOutcome
    }

    if (!layout.rows[rowIndex]?.cells[cellIndex]) {
      onDropped(CELL_GONE)
      return 'dropped'
    }
    const newRows = layout.rows.map((row, i) => {
      if (i !== rowIndex) return row
      return {
        ...row,
        cells: row.cells.map((c, j) => (j === cellIndex ? finalCell : c)),
      }
    })
    commitLayout({ ...layout, rows: newRows }, pageId)
    return saveOutcome
  }, [])

  return { saveCell }
}

export default useDashboardCellSave
